import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { API_V1, getImageUrl, fetchApi } from '../config/api';
import { getExtraCartId } from '../utils/cartExtraId';
import { isWasabiGingerExtra } from '../constants/wasabiGinger';
import { fallbackImageForExtra } from '../utils/extraImages';
import { refAmount } from '../utils/money';
import {
  getNumCombinaciones,
  getRollsComboToppingConfig,
  isTexturaFrios,
  isTexturaCombinado,
  isRollEspeciales,
  isComplementoConTodo,
  proteinaBloqueaConTodo,
  complementosRollPorRoll,
  mapAdditionalOptionNames,
  mapRollProducts,
  isCombinacionCompleta,
  getCombinacionesIniciales,
  getCombinacionesDeLaCasa,
  esComboGrande,
  etiquetaGrupoRolls,
  saucesConFuji,
  maxSalsasDelCombo,
} from '../constants/comboCombinaciones';
import './ProductDetail.css';

/** Salsas de rolls que no aplican a combos de pollo. */
const POLLO_EXCLUDED_SAUCE_NAMES = ['ANGUILA', 'FUJI', 'SOYA'];

function normalizeProductName(name) {
  return String(name || '')
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function extrasVisibles(combo) {
  const list = [];
  const seen = new Set();
  const push = (extra) => {
    if (!extra) return;
    const id = String(extra.id ?? extra.extra_id ?? '');
    if (id === '' || seen.has(id)) return;
    seen.add(id);
    list.push(extra);
  };
  (combo?.extras || []).forEach((extra) => {
    push(extra);
    (extra.extras || []).forEach(push);
  });
  return list;
}

function isPolloExcludedSauce(productName) {
  const n = normalizeProductName(productName);
  return POLLO_EXCLUDED_SAUCE_NAMES.some((excluded) => n.includes(excluded));
}

function pickIncludedGroup(groups, type) {
  if (!Array.isArray(groups)) return null;
  return groups.find((g) => String(g.type || '').toLowerCase() === type) || null;
}

function mapIncludedProducts(group) {
  if (!group || !Array.isArray(group.products)) return [];
  return group.products.map((p) => ({
    id: p.id,
    product_name: p.product_name || p.name || 'Producto',
    default_quantity: Math.max(0, Number(p.default_quantity) || 0),
    xetux_product_id: p.xetux_product_id ?? null,
    xetux_item_id: p.xetux_item_id ?? null,
    xetux_family_id: p.xetux_family_id ?? null,
  }));
}

function buildDefaultSauceQty(products) {
  const qty = {};
  products.forEach((p) => {
    qty[p.id] = p.default_quantity;
  });
  return qty;
}

const ComboDetail = () => {
  const { comboId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [combo, setCombo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [combinaciones, setCombinaciones] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [modoArmado, setModoArmado] = useState('');
  const [conSesamo, setConSesamo] = useState(null);
  const [conQuesoCrema, setConQuesoCrema] = useState(true);
  const [conCebollin, setConCebollin] = useState(true);
  const [sauceProducts, setSauceProducts] = useState([]);
  const [sauceMaxQuantity, setSauceMaxQuantity] = useState(0);
  const [sauceQuantities, setSauceQuantities] = useState({});
  const [drinkProducts, setDrinkProducts] = useState([]);
  const [selectedDrinkId, setSelectedDrinkId] = useState('');
  const [texturas, setTexturas] = useState([]);
  const [rolls, setRolls] = useState([]);
  const [proteinas, setProteinas] = useState([]);
  const [complementos, setComplementos] = useState([]);

  useEffect(() => {
    const fetchCombo = async () => {
      if (!comboId) return;
      try {
        setError(null);
        const res = await fetchApi(`${API_V1}/combos/${comboId}`);
        if (!res.ok) throw new Error('Combo no encontrado');
        const data = await res.json();
        const c = data.data ?? data;
        const rollsCount = c.rolls_count != null ? Number(c.rolls_count) : 0;
        const category = (c.category || '').toLowerCase();
        const isPollo = category === 'pollo-crispy';
        const isRolls = category === 'rolls-mixtos';
        const numComb = isRolls ? getNumCombinaciones(rollsCount) : 0;
        const groups = Array.isArray(c.included_groups) ? c.included_groups : [];

        const sauceGroup = pickIncludedGroup(groups, 'sauce');
        let sauces = mapIncludedProducts(sauceGroup);
        if (isPollo) {
          sauces = sauces.filter((p) => !isPolloExcludedSauce(p.product_name));
        } else if (isRolls) {
          sauces = saucesConFuji(sauces, c.name);
        }
        const maxSauce = sauces.length
          ? maxSalsasDelCombo(
              c.name,
              Math.max(1, Number(sauceGroup?.max_quantity) || 1)
            )
          : 0;

        const drinkGroup = pickIncludedGroup(groups, 'drink');
        const drinks = mapIncludedProducts(drinkGroup);

        let nextTexturas = [];
        let nextRolls = [];
        let nextProteinas = [];
        let nextComplementos = [];
        if (isRolls) {
          try {
            const optRes = await fetchApi(
              `${API_V1}/xetux/catalogue/roll-combination-options`
            );
            if (optRes.ok) {
              const optJson = await optRes.json();
              const optData = optJson.data ?? optJson;
              nextTexturas = mapAdditionalOptionNames(optData.texturas).filter(
                (name) => !isTexturaCombinado(name)
              );
              nextRolls = mapRollProducts(optData.rolls);
              nextProteinas = mapAdditionalOptionNames(optData.proteinas);
              nextComplementos = mapAdditionalOptionNames(optData.complementos);
            }
          } catch {
            // Si falla Xetux, se dejan los selects vacíos; el usuario no podrá avanzar.
          }
        }

        setCombo({
          id: c.id ?? c.combo_id,
          name: c.name || 'Combo',
          description: c.description || '',
          price: refAmount(c.price_eur),
          image: getImageUrl(c.image_url ?? c.image ?? c.thumbnail ?? c.thumbnail_url),
          extras: c.extras ?? [],
          category,
          rolls_count: rollsCount,
          includes_drink: Boolean(c.includes_drink),
          xetux_product_id: c.xetux_product_id ?? null,
          xetux_item_id: c.xetux_item_id ?? null,
        });

        setTexturas(nextTexturas);
        setRolls(nextRolls);
        setProteinas(nextProteinas);
        setComplementos(nextComplementos);
        const usarCasa = isRolls && esComboGrande(rollsCount) && numComb > 0;
        setCombinaciones(
          numComb > 0
            ? usarCasa
              ? getCombinacionesDeLaCasa(numComb)
              : getCombinacionesIniciales(numComb)
            : []
        );
        setModoArmado(usarCasa ? 'casa' : '');
        setCurrentStep(0);

        setSauceProducts(sauces);
        setSauceMaxQuantity(maxSauce);
        // Rolls: partir de defaults del admin. Pollo: el usuario elige (sin ANGUILA/FUJI/SOYA).
        setSauceQuantities(
          sauces.length
            ? isPollo
              ? Object.fromEntries(sauces.map((p) => [p.id, 0]))
              : buildDefaultSauceQty(sauces)
            : {}
        );

        setDrinkProducts(drinks);
        setSelectedDrinkId(drinks.length === 1 ? String(drinks[0].id) : '');

        setConSesamo(null);
        setConQuesoCrema(true);
        setConCebollin(true);
      } catch (err) {
        setError(err.message);
        setCombo(null);
        setCombinaciones([]);
        setTexturas([]);
        setRolls([]);
        setProteinas([]);
        setComplementos([]);
        setSauceProducts([]);
        setSauceQuantities({});
        setSauceMaxQuantity(0);
        setDrinkProducts([]);
        setSelectedDrinkId('');
        setModoArmado('');
        setConSesamo(null);
        setConQuesoCrema(true);
        setConCebollin(true);
      } finally {
        setLoading(false);
      }
    };
    fetchCombo();
  }, [comboId]);

  useEffect(() => {
    const hay = combinaciones.some((c) => isTexturaFrios(c.textura));
    if (!hay) {
      setConSesamo(null);
    }
  }, [combinaciones]);

  const esGrande = esComboGrande(combo?.rolls_count);
  const modoEfectivo = esGrande ? modoArmado : 'roll';
  const tieneTopping = Boolean(
    combo?.category === 'rolls-mixtos' && getRollsComboToppingConfig(combo)
  );

  const steps = useMemo(() => {
    const list = [];
    const grande = esComboGrande(combo?.rolls_count);
    const modoPendiente = Boolean(
      combo?.category === 'rolls-mixtos' && grande && modoArmado !== 'roll' && modoArmado !== 'casa'
    );
    if (combo?.category === 'rolls-mixtos') {
      if (grande) {
        list.push({ type: 'modo' });
      }
      const armarRollPorRoll = !grande || modoArmado === 'roll';
      const armarCasa = modoArmado === 'casa';
      if (!modoPendiente && armarRollPorRoll) {
        combinaciones.forEach((_, index) => {
          list.push({ type: 'combinacion', index });
        });
      }
      if (!modoPendiente && tieneTopping && (armarRollPorRoll || armarCasa)) {
        list.push({ type: 'toppings' });
      }
      // La recomendación del chef no pregunta queso crema ni cebollín.
      if (!modoPendiente && armarRollPorRoll) {
        list.push({ type: 'acabados' });
      }
    }
    if (!modoPendiente && sauceProducts.length > 0) {
      list.push({ type: 'sauces' });
    }
    if (!modoPendiente && drinkProducts.length > 0) {
      list.push({ type: 'drink' });
    }
    return list;
  }, [
    combo,
    combinaciones,
    modoArmado,
    tieneTopping,
    sauceProducts,
    drinkProducts,
  ]);

  const currentStepConfig = steps[currentStep] || null;
  const hasCustomizerSteps = steps.length > 0;

  const handleQuantityChange = (delta) => {
    setQuantity((q) => Math.max(1, q + delta));
  };

  const updateCombinacion = (index, field, value) => {
    setCombinaciones((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const applyProteinChange = (index, value) => {
    setCombinaciones((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const complemento =
          proteinaBloqueaConTodo(value) && isComplementoConTodo(row.complemento)
            ? ''
            : row.complemento;
        return { ...row, proteina: value, complemento };
      })
    );
  };

  const applyRollChange = (index, value) => {
    const selected = rolls.find((roll) => roll.name === value);
    setCombinaciones((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        return {
          ...row,
          roll: value,
          roll_product_id: selected?.id ?? null,
          proteina: isRollEspeciales(value) ? row.proteina : '',
        };
      })
    );
  };

  const sauceQtySum = sauceProducts.reduce(
    (sum, p) => sum + (Number(sauceQuantities[p.id]) || 0),
    0
  );

  const saucesComplete =
    sauceProducts.length === 0 ||
    (sauceQtySum === sauceMaxQuantity && sauceQtySum > 0);

  const drinkComplete =
    drinkProducts.length === 0 || Boolean(selectedDrinkId);

  const combinacionLista = (row) => {
    if (!isCombinacionCompleta(row)) return false;
    if (modoEfectivo === 'casa') return true;
    return !isComplementoConTodo(row.complemento);
  };

  const allCombinacionesFilled =
    combo?.category !== 'rolls-mixtos' ||
    (modoEfectivo !== '' &&
      combinaciones.length > 0 &&
      combinaciones.every((c) => combinacionLista(c)));

  const texturasDisponibles = texturas.filter((t) => !isTexturaCombinado(t));
  const tieneTexturaCombinado = texturasDisponibles.some((t) =>
    String(t || '')
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .includes('COMBINADO')
  );
  const hayTexturaFrios = combinaciones.some((c) => isTexturaFrios(c.textura));

  const toppingElegido = (row) =>
    Boolean(row?.topping_crunch) !== Boolean(row?.topping_ensalada);

  const toppingsCompletos = !tieneTopping || combinaciones.every(toppingElegido);

  const puedeAnadirCombo =
    allCombinacionesFilled &&
    saucesComplete &&
    drinkComplete &&
    toppingsCompletos &&
    (!hayTexturaFrios || conSesamo !== null);

  const isCurrentStepComplete = () => {
    if (!currentStepConfig) return false;
    if (currentStepConfig.type === 'modo') {
      return modoArmado === 'roll' || modoArmado === 'casa';
    }
    if (currentStepConfig.type === 'combinacion') {
      return combinacionLista(combinaciones[currentStepConfig.index]);
    }
    if (currentStepConfig.type === 'toppings') return toppingsCompletos;
    if (currentStepConfig.type === 'acabados') {
      return !hayTexturaFrios || conSesamo !== null;
    }
    if (currentStepConfig.type === 'sauces') return saucesComplete;
    if (currentStepConfig.type === 'drink') return drinkComplete;
    return false;
  };

  const elegirModo = (modo) => {
    const num =
      combinaciones.length || getNumCombinaciones(Number(combo?.rolls_count) || 0);
    setModoArmado(modo);
    setConSesamo(null);
    if (modo === 'casa') {
      setConQuesoCrema(true);
      setConCebollin(true);
    }
    setCombinaciones(
      modo === 'casa' ? getCombinacionesDeLaCasa(num) : getCombinacionesIniciales(num)
    );
    setCurrentStep(0);
  };

  const toggleTopping = (index, field) => {
    setCombinaciones((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const selected = !row[field];
        if (field === 'topping_crunch') {
          return {
            ...row,
            topping_crunch: selected,
            topping_ensalada: selected ? false : row.topping_ensalada,
          };
        }
        return {
          ...row,
          topping_ensalada: selected,
          topping_crunch: selected ? false : row.topping_crunch,
        };
      })
    );
  };

  const handleNextStep = () => {
    if (isCurrentStepComplete() && currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const updateSauceQty = (productId, nextQty) => {
    const safe = Math.max(0, Math.floor(Number(nextQty) || 0));
    setSauceQuantities((prev) => {
      const others = sauceProducts.reduce((sum, p) => {
        if (String(p.id) === String(productId)) return sum;
        return sum + (Number(prev[p.id]) || 0);
      }, 0);
      const capped = Math.min(safe, Math.max(0, sauceMaxQuantity - others));
      return { ...prev, [productId]: capped };
    });
  };

  const handleAddToCart = () => {
    if (!combo) return;
    if (hasCustomizerSteps && !puedeAnadirCombo) return;

    const payload = {
      id: `combo-${combo.id}`,
      name: combo.name,
      price: String(combo.price),
      quantity,
      image: combo.image,
      type: 'combo',
      comboId: combo.id,
      xetuxProductId: combo.xetux_product_id,
      xetuxItemId: combo.xetux_item_id,
    };

    if (combo.category === 'rolls-mixtos' && combinaciones.length > 0) {
      payload.combinaciones = combinaciones.map((row) => ({
        ...row,
        topping_crunch: Boolean(row.topping_crunch),
        topping_ensalada: Boolean(row.topping_ensalada),
      }));
      if (hayTexturaFrios) {
        payload.rollsConSesamo = conSesamo;
      }
      const recomendacionChef = modoEfectivo === 'casa';
      payload.rollsConQuesoCrema = recomendacionChef ? true : conQuesoCrema;
      payload.rollsConCebollin = recomendacionChef ? true : conCebollin;
    }

    if (sauceProducts.length > 0) {
      payload.includedSauces = sauceProducts
        .map((p) => ({
          id: p.id,
          product_name: p.product_name,
          quantity: Number(sauceQuantities[p.id]) || 0,
          xetux_product_id: p.xetux_product_id,
          xetux_item_id: p.xetux_item_id,
        }))
        .filter((p) => p.quantity > 0);
    }

    if (drinkProducts.length > 0 && selectedDrinkId) {
      const drink = drinkProducts.find(
        (p) => String(p.id) === String(selectedDrinkId)
      );
      if (drink) {
        payload.includedDrink = {
          id: drink.id,
          product_name: drink.product_name,
          quantity: 1,
          xetux_product_id: drink.xetux_product_id,
          xetux_item_id: drink.xetux_item_id,
        };
      }
    }

    addToCart(payload);
  };

  const handleAddExtraToCart = (extra) => {
    const extraName = extra.title ?? extra.name ?? '';
    if (extra.requires_flavor || isWasabiGingerExtra(extraName)) {
      const extraId = extra.id ?? extra.extra_id;
      if (extraId != null) navigate(`/extra/${extraId}`);
      return;
    }
    const price = refAmount(extra.price_eur);
    let image = null;
    if (extra.image_url) {
      image = getImageUrl(extra.image_url);
    } else if (extra.thumbnail || extra.thumbnail_url || extra.image) {
      image = getImageUrl(extra.thumbnail ?? extra.thumbnail_url ?? extra.image);
    }
    addToCart({
      id: getExtraCartId('combo', comboId, extra.id ?? '', extra.title ?? extra.name),
      name: extra.title ?? extra.name,
      price,
      quantity: 1,
      image,
      type: 'extra',
      extraId: extra.id ?? extra.extra_id ?? null,
      xetuxProductId: extra.xetux_product_id ?? null,
      xetuxItemId: extra.xetux_item_id ?? null,
    });
  };

  if (loading) {
    return (
      <section className="product-detail">
        <div className="container">
          <p className="combos-loading">Cargando combo...</p>
        </div>
      </section>
    );
  }

  if (error || !combo) {
    return (
      <section className="product-detail">
        <div className="container">
          <p className="combos-error">{error || 'Combo no encontrado.'}</p>
          <Link to="/combos">Volver a Combos</Link>
        </div>
      </section>
    );
  }

  const combIndex =
    currentStepConfig?.type === 'combinacion' ? currentStepConfig.index : 0;

  return (
    <section className="product-detail">
      <div className="container">
        <div className="breadcrumb">
          <Link to="/">INICIO</Link>
          <span> / </span>
          <Link to="/combos">COMBOS</Link>
          <span> / </span>
          <span>{combo.name}</span>
        </div>

        <div className="product-detail-content">
          <div className="product-detail-left">
            <div className="product-detail-image-container">
              {combo.image ? (
                <img
                  src={combo.image}
                  alt={combo.name}
                  className="product-detail-image"
                />
              ) : (
                <div className="product-detail-image-placeholder" />
              )}
            </div>
          </div>

          <div className="product-detail-right">
            <div className="product-detail-header">
              <h1 className="product-detail-name">{combo.name}</h1>
            </div>

            {combo.description && (
              <p className="product-detail-description">{combo.description}</p>
            )}

            <div className="product-price">
              <span className="price-label">Ref</span>
              <span className="price-value">{refAmount(combo.price)}</span>
            </div>

            <div className="product-actions">
              <div className="quantity-selector">
                <button
                  className="quantity-btn"
                  onClick={() => handleQuantityChange(-1)}
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <span className="quantity-value">{quantity}</span>
                <button className="quantity-btn" onClick={() => handleQuantityChange(1)}>
                  +
                </button>
              </div>
              {!hasCustomizerSteps && (
                <button className="add-to-cart-main-btn" onClick={handleAddToCart}>
                  Añadir al Carrito
                </button>
              )}
            </div>

            {hasCustomizerSteps && (
              <div className="combo-combinaciones">
                <div className="combo-stepper-header">
                  <h3 className="combo-combinaciones-title">
                    {currentStepConfig?.type === 'sauces'
                      ? 'Elige tus salsas'
                      : currentStepConfig?.type === 'drink'
                        ? 'Elige tu bebida'
                        : currentStepConfig?.type === 'modo'
                          ? '¿Cómo quieres armar el combo?'
                          : currentStepConfig?.type === 'toppings'
                            ? 'Toppings'
                            : currentStepConfig?.type === 'acabados'
                              ? hayTexturaFrios
                                ? 'Sésamo, queso crema y cebollín'
                                : 'Queso crema y cebollín'
                              : etiquetaGrupoRolls(currentStepConfig?.index ?? 0)}
                  </h3>
                  <div className="combo-stepper-progress">
                    <span className="combo-stepper-progress-text">
                      Paso {currentStep + 1} de {steps.length}
                    </span>
                    <div className="combo-stepper-dots">
                      {steps.map((step, index) => (
                        <div
                          key={`${step.type}-${step.index ?? index}`}
                          className={`combo-stepper-dot ${
                            index < currentStep
                              ? 'completed'
                              : index === currentStep
                                ? 'active'
                                : ''
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="combo-combinacion-stepper">
                  {currentStepConfig?.type === 'combinacion' && (
                    <>
                      <div className="combo-combinacion-row">
                        <span className="combo-combinacion-label">
                          {etiquetaGrupoRolls(combIndex)}
                        </span>
                        <div className="combo-combinacion-selects">
                          <div className="combo-combinacion-field">
                            <select
                              value={combinaciones[combIndex].textura}
                              onChange={(e) =>
                                updateCombinacion(combIndex, 'textura', e.target.value)
                              }
                              className="combo-combinacion-select"
                              aria-label={`Textura combinación ${combIndex + 1}`}
                            >
                              <option value="">Textura</option>
                              {texturasDisponibles.map((t) => (
                                <option key={t} value={t}>
                                  {t}
                                </option>
                              ))}
                            </select>
                              <p className="combo-chef-hint">
                                Selecciona la textura para esta combinación.
                              </p>
                          </div>
                          <div className="combo-combinacion-field">
                            <select
                              value={combinaciones[combIndex].roll}
                              onChange={(e) =>
                                applyRollChange(combIndex, e.target.value)
                              }
                              className="combo-combinacion-select"
                              aria-label={`Rolls combinación ${combIndex + 1}`}
                            >
                              <option value="">Rolls</option>
                              {rolls.map((roll) => (
                                <option key={roll.id ?? roll.name} value={roll.name}>
                                  {roll.name}
                                </option>
                              ))}
                            </select>
                            <p className="combo-chef-hint">
                              Selecciona el roll para esta combinación.
                            </p>
                          </div>
                          {isRollEspeciales(combinaciones[combIndex].roll) && (
                            <div className="combo-combinacion-field">
                              <select
                                value={combinaciones[combIndex].proteina}
                                onChange={(e) =>
                                  applyProteinChange(combIndex, e.target.value)
                                }
                                className="combo-combinacion-select"
                                aria-label={`Tipo de rolls especiales combinación ${combIndex + 1}`}
                              >
                                <option value="">Tipo de rolls especiales</option>
                                {proteinas.map((p) => (
                                  <option key={p} value={p}>
                                    {p}
                                  </option>
                                ))}
                              </select>
                              <p className="combo-chef-hint">
                                Elige el tipo de 10 rolls especiales.
                              </p>
                            </div>
                          )}
                          <div className="combo-combinacion-field">
                            <select
                              value={
                                isComplementoConTodo(combinaciones[combIndex].complemento)
                                  ? ''
                                  : combinaciones[combIndex].complemento
                              }
                              onChange={(e) =>
                                updateCombinacion(
                                  combIndex,
                                  'complemento',
                                  e.target.value
                                )
                              }
                              className="combo-combinacion-select"
                              aria-label={`Complemento combinación ${combIndex + 1}`}
                            >
                              <option value="">Complemento</option>
                              {complementosRollPorRoll(complementos).map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                            <p className="combo-chef-hint">
                              Elige solo plátano, solo aguacate o solo proteína.
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {currentStepConfig?.type === 'modo' && (
                    <fieldset className="combo-topping-fieldset">
                      <legend className="combo-topping-legend">Armado</legend>
                      <div className="combo-topping-options" role="radiogroup" aria-label="Modo de armado">
                        <label className="combo-topping-option">
                          <input
                            type="radio"
                            name={`combo-modo-${combo.id}`}
                            checked={modoArmado === 'roll'}
                            onChange={() => elegirModo('roll')}
                          />
                          <span className="combo-topping-option-text">
                            Roll por roll. Eliges textura, rolls y complemento de cada grupo de 10.
                          </span>
                        </label>
                        <label className="combo-topping-option">
                          <input
                            type="radio"
                            name={`combo-modo-${combo.id}`}
                            checked={modoArmado === 'casa'}
                            onChange={() => elegirModo('casa')}
                          />
                          <span className="combo-topping-option-text">
                            Recomendación del chef: rolls combinados, mixtos y con todo.
                          </span>
                        </label>
                      </div>
                    </fieldset>
                  )}

                  {currentStepConfig?.type === 'toppings' && (
                    <fieldset className="combo-topping-fieldset">
                      <legend className="combo-topping-legend">Topping de cada combinación</legend>
                      <p className="combo-topping-hint">
                        Elige un solo topping por cada grupo de 10 rolls: crunch o ensalada. No tiene costo extra.
                      </p>
                      <div className="combo-topping-options">
                        {combinaciones.map((row, index) => (
                          <div key={`topping-${index}`} className="combo-topping-piece">
                            <span className="combo-combinacion-label">
                              {etiquetaGrupoRolls(index)}
                            </span>
                            <label className="combo-sesamo-label combo-queso-cebollin-label">
                              <input
                                type="checkbox"
                                className="combo-sesamo-checkbox"
                                checked={Boolean(row.topping_crunch)}
                                onChange={() => toggleTopping(index, 'topping_crunch')}
                              />
                              <span>Topping crunch</span>
                            </label>
                            <label className="combo-sesamo-label combo-queso-cebollin-label">
                              <input
                                type="checkbox"
                                className="combo-sesamo-checkbox"
                                checked={Boolean(row.topping_ensalada)}
                                onChange={() => toggleTopping(index, 'topping_ensalada')}
                              />
                              <span>Topping de ensalada dinamita</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </fieldset>
                  )}

                  {currentStepConfig?.type === 'acabados' && (
                    <>
                      {hayTexturaFrios && (
                        <div className="combo-combinacion-field">
                          <label className="combo-sesamo-label" htmlFor="sesamo-rolls">
                            ¿Sésamo en los rolls fríos?
                          </label>
                          <select
                            id="sesamo-rolls"
                            className="combo-combinacion-select"
                            value={conSesamo === null ? '' : conSesamo ? 'si' : 'no'}
                            onChange={(e) => {
                              const value = e.target.value;
                              setConSesamo(value === '' ? null : value === 'si');
                            }}
                            aria-label="Sésamo en los rolls fríos"
                          >
                            <option value="">Elige si quieres sésamo</option>
                            <option value="si">Con sésamo</option>
                            <option value="no">Sin sésamo</option>
                          </select>
                          <p className="combo-chef-hint">
                            El sésamo solo va en los rolls fríos. Los tempura no llevan sésamo.
                          </p>
                        </div>
                      )}
                      <div className="combo-sesamo-row">
                        <label className="combo-sesamo-label combo-queso-cebollin-label">
                          <input
                            type="checkbox"
                            className="combo-sesamo-checkbox"
                            checked={conQuesoCrema}
                            onChange={(e) => setConQuesoCrema(e.target.checked)}
                          />
                          <span>Confirmo que deseo los rolls con queso crema</span>
                        </label>
                      </div>
                      <div className="combo-sesamo-row">
                        <label className="combo-sesamo-label combo-queso-cebollin-label">
                          <input
                            type="checkbox"
                            className="combo-sesamo-checkbox"
                            checked={conCebollin}
                            onChange={(e) => setConCebollin(e.target.checked)}
                          />
                          <span>Confirmo que deseo los rolls con cebollín</span>
                        </label>
                      </div>
                    </>
                  )}

                  {currentStepConfig?.type === 'sauces' && (
                    <div className="combo-included-section">
                      <p className="combo-included-hint">
                        Distribuye exactamente {sauceMaxQuantity} salsa
                        {sauceMaxQuantity === 1 ? '' : 's'} incluidas en el combo
                        (sin costo adicional).
                      </p>
                      <p
                        className={`combo-sauce-sum ${
                          saucesComplete ? 'is-ok' : 'is-pending'
                        }`}
                      >
                        Seleccionadas: {sauceQtySum} / {sauceMaxQuantity}
                      </p>
                      <div className="combo-sauce-list">
                        {sauceProducts.map((product) => {
                          const qty = Number(sauceQuantities[product.id]) || 0;
                          return (
                            <div key={product.id} className="combo-sauce-row">
                              <span className="combo-sauce-name">
                                {product.product_name}
                              </span>
                              <div className="combo-sauce-qty">
                                <button
                                  type="button"
                                  className="combo-sauce-qty-btn"
                                  onClick={() => updateSauceQty(product.id, qty - 1)}
                                  disabled={qty <= 0}
                                  aria-label={`Menos ${product.product_name}`}
                                >
                                  −
                                </button>
                                <span className="combo-sauce-qty-value">{qty}</span>
                                <button
                                  type="button"
                                  className="combo-sauce-qty-btn"
                                  onClick={() => updateSauceQty(product.id, qty + 1)}
                                  disabled={sauceQtySum >= sauceMaxQuantity}
                                  aria-label={`Más ${product.product_name}`}
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {currentStepConfig?.type === 'drink' && (
                    <div className="combo-included-section">
                      <p className="combo-included-hint">
                        Elige exactamente 1 bebida incluida en el combo.
                      </p>
                      <div
                        className="combo-drink-options"
                        role="radiogroup"
                        aria-label="Bebida incluida"
                      >
                        {drinkProducts.map((product) => (
                          <label key={product.id} className="combo-drink-option">
                            <input
                              type="radio"
                              name={`combo-included-drink-${combo.id}`}
                              value={product.id}
                              checked={String(selectedDrinkId) === String(product.id)}
                              onChange={() => setSelectedDrinkId(String(product.id))}
                            />
                            <span>{product.product_name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="combo-stepper-navigation">
                    <button
                      type="button"
                      className="combo-stepper-btn combo-stepper-btn-prev"
                      onClick={handlePreviousStep}
                      disabled={currentStep === 0}
                    >
                      ← Anterior
                    </button>
                    {currentStep < steps.length - 1 ? (
                      <button
                        type="button"
                        className="combo-stepper-btn combo-stepper-btn-next"
                        onClick={handleNextStep}
                        disabled={!isCurrentStepComplete()}
                      >
                        Siguiente →
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="add-to-cart-main-btn combo-stepper-add-btn"
                        disabled={!puedeAnadirCombo}
                        onClick={handleAddToCart}
                      >
                        Añadir al Carrito
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {extrasVisibles(combo).length > 0 && (
              <div className="extra-sauces">
                <h3 className="extra-sauces-title">Extras</h3>
                <div className="sauces-list">
                  {extrasVisibles(combo).map((extra) => {
                    let extraImageUrl = fallbackImageForExtra(extra.title ?? extra.name);
                    if (extra.image_url) {
                      extraImageUrl = getImageUrl(extra.image_url);
                    }

                    return (
                      <div key={extra.id} className="sauce-item">
                        <div className="sauce-icon">
                          {extraImageUrl ? (
                            <img
                              src={extraImageUrl}
                              alt={extra.title ?? extra.name}
                              className="sauce-image"
                              onError={(e) => {
                                const fallback = fallbackImageForExtra(
                                  extra.title ?? extra.name
                                );
                                if (fallback && e.target.src !== fallback) {
                                  e.target.src = fallback;
                                }
                              }}
                            />
                          ) : null}
                        </div>
                        <div className="sauce-info">
                          <span className="sauce-name">
                            {extra.title ?? extra.name}
                          </span>
                          <span className="sauce-price">
                            Ref {refAmount(extra.price_eur)}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="sauce-add-btn"
                          onClick={() => handleAddExtraToCart(extra)}
                        >
                          {extra.requires_flavor
                            ? 'Elegir sabor'
                            : isWasabiGingerExtra(extra.title ?? extra.name)
                              ? 'Elegir combinación'
                              : 'Añadir al Carrito'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComboDetail;
